import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  Snackbar,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import dayjs from 'dayjs';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Configure dayjs to handle timezones
dayjs.extend(utc);
dayjs.extend(timezone);

// TODO: Replace 'YOUR_SERVER_IP' with your actual server IP address

// Use proxy in local development to avoid CORS issues
const WHATSAPP_API_URL = import.meta.env.DEV && !Capacitor.isNativePlatform()
  ? '/webhook/whatsapp'
  : 'https://wts.pfplus.cc/webhook/whatsapp';

const WHATSAPP_TARGET_NUMBER = '85292892534';
const SECOND_WHATSAPP_TARGET_NUMBER = '85262006113'; // Added for the second message

function RequestForm() {
  const [requestType, setRequestType] = useState('');
  const [startDateTime, setStartDateTime] = useState(dayjs());
  const [durationType, setDurationType] = useState('days'); // 'days' or 'endDate'
  const [duration, setDuration] = useState('1');
  const [endDate, setEndDate] = useState(dayjs().add(1, 'day'));
  const [details, setDetails] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  // const [errorMessage, setErrorMessage] = useState(''); // Replaced by snackbarMessage and snackbarSeverity
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info'); // Can be 'success', 'error', 'warning', 'info'
  // Add new state for calendar dialog
  const [openCalendarDialog, setOpenCalendarDialog] = useState(false);
  const formRef = useRef(null); // Create a form reference
  const [isSubmittingWhatsApp, setIsSubmittingWhatsApp] = useState(false); // New state for WhatsApp submission

  // Error states for validation
  const [errors, setErrors] = useState({
    requestType: false,
    details: false
  });

  // Handle validation
  const validateForm = () => {
    const newErrors = {
      requestType: !requestType,
      details: !details.trim()
    };
    setErrors(newErrors);
    
    // Return true if no errors
    return !Object.values(newErrors).some(Boolean);
  };

  const handleDurationTypeChange = (event, newType) => {
    if (newType !== null) {
      setDurationType(newType);
      if (newType === 'days') {
        setDuration('1');
        setEndDate(dayjs(startDateTime).add(1, 'day'));
      } else {
        setEndDate(dayjs(startDateTime).add(1, 'day'));
        setDuration(endDate.diff(startDateTime, 'day').toString());
      }
    }
  };

  const handleDurationChange = (value) => {
    setDuration(value);
    setEndDate(dayjs(startDateTime).add(Number(value), 'day'));
  };

  const handleEndDateChange = (value) => {
    const newEndDate = dayjs(value);
    if (newEndDate.isBefore(startDateTime, 'day')) {
      // If the new end date is before the start date, reset it to the start date
      setEndDate(startDateTime.add(1, 'day'));
      setDuration('1');
    } else {
      setEndDate(newEndDate);
      setDuration(newEndDate.diff(startDateTime, 'day').toString());
    }
  };


  const handleWhatsAppSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      setSnackbarMessage('請填寫所有必填欄位後再提交。');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    // Generate Google Calendar URL (similar to handleSubmit)
    const startTimeCal = startDateTime.format('YYYYMMDD[T]HHmmss');
    const endTimeCal = durationType === 'days' ? dayjs(startDateTime).add(Number(duration), 'day').format('YYYYMMDD[T]HHmmss') : endDate.format('YYYYMMDD[T]HHmmss');
    const googleCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(`Supercloud Request - ${requestType}`)}&dates=${startTimeCal}/${endTimeCal}&details=${encodeURIComponent(details)}`;

    // Construct the pre-filled message for the wa.me link
    const prefilledMessageForReply = `Regarding request:
Type: ${requestType}
Details: ${details}

This request is approved✅`;

    const encodedPrefilledMessage = encodeURIComponent(prefilledMessageForReply);
    const replyToWhatsAppNumber = "85262006113"; // The number to reply to via wa.me
    const replyToLink = `https://wa.me/${replyToWhatsAppNumber}?text=${encodedPrefilledMessage}`;

    const messageBodyForPrimaryRecipient = `
Request Type: ${requestType}

Start Date and Time: ${startDateTime.format('YYYY-MM-DD HH:mm')}
${durationType === 'days' ?
        `Duration: ${duration} days` :
        `End Date and Time: ${endDate.format('YYYY-MM-DD HH:mm')}`}

Details: ${details}

Add to Google Calendar: ${googleCalUrl}

Confirm : ${replyToLink}
    `.trim();

    const whatsappUrl = `https://wa.me/${WHATSAPP_TARGET_NUMBER}?text=${encodeURIComponent(messageBodyForPrimaryRecipient)}`;
    
    if (Capacitor.isNativePlatform()) {
      Browser.open({ url: whatsappUrl }).catch(err => console.error('Browser open error:', err));
    } else {
      window.open(whatsappUrl, '_blank');
    }
    
    setSnackbarMessage('Opening WhatsApp...');
    setSnackbarSeverity('success');
    setOpenSnackbar(true);
    setOpenCalendarDialog(true);
  };

  // Add new function to handle adding to Google Calendar
  const addToGoogleCalendar = async (e) => { // Make it async and accept event
    e.preventDefault(); // Prevent default button behavior

    if (formRef.current.reportValidity()) {
      try {
        // Format dates for Google Calendar URL
        const startTime = startDateTime.format('YYYYMMDD[T]HHmmss');
        const endTime = durationType === 'days' ? dayjs(startDateTime).add(Number(duration), 'day').format('YYYYMMDD[T]HHmmss') : endDate.format('YYYYMMDD[T]HHmmss');

        // Create Google Calendar URL
        const googleCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(`Supercloud Request - ${requestType}`)}&dates=${startTime}/${endTime}&details=${encodeURIComponent(details)}`;

        // Open Google Calendar using Capacitor Browser plugin
        if (Capacitor.isNativePlatform()) {
          await Browser.open({ url: googleCalUrl });
        } else {
          window.open(googleCalUrl, '_blank');
        }

        setOpenCalendarDialog(false);

      } catch (error) {
        console.error('Calendar error:', error);
        setSnackbarMessage('Unable to open Google Calendar. Please try again.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        setOpenCalendarDialog(false);
      }
    }
  };

  return (
    <Box component="form" noValidate ref={formRef} sx={{ px: 2 }}> {/* Attach the form reference */}
      <Typography sx={{ mb: 2, color: 'primary.main' }}>
        Submit Request (5 days in adv)
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }} error={errors.requestType}>
        <Select
          value={requestType}
          onChange={(e) => {
            setRequestType(e.target.value);
            if (e.target.value) setErrors({ ...errors, requestType: false });
          }}
          displayEmpty
          required
        >
          <MenuItem value="" disabled>Select Request Type</MenuItem>
          <MenuItem value="SuSu EYT">Apply SuSu EYT</MenuItem>
          <MenuItem value="Out Town">Apply Out Town</MenuItem>
          <MenuItem value="Ar Bing">Apply ArBing Activity</MenuItem>
        </Select>
        {errors.requestType && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
            請選擇申請類型
          </Typography>
        )}
      </FormControl>

      <TextField
        type="datetime-local"
        fullWidth
        label="Start Date and Time"
        InputLabelProps={{ shrink: true }}
        value={startDateTime.format('YYYY-MM-DDTHH:mm')}
        onChange={(e) => setStartDateTime(dayjs(e.target.value))}
        sx={{ mb: 3 }}
        required
      />

      <Grid container spacing={1} sx={{ mb: 3 }}>
        <Grid item xs={7} sm={8}>
          {durationType === 'days' ? (
            <TextField
              type="number"
              fullWidth
              label="Duration (Days)"
              value={duration}
              onChange={(e) => handleDurationChange(e.target.value)}
              InputProps={{ inputProps: { min: 1 } }}
              required
            />
          ) : (
            <TextField
              type="datetime-local"
              fullWidth
              label="End Date and Time"
              InputLabelProps={{ shrink: true }}
              value={endDate.format('YYYY-MM-DDTHH:mm')}
              onChange={(e) => handleEndDateChange(e.target.value)}
              required
              inputProps={{
                min: startDateTime.format('YYYY-MM-DDTHH:mm'), // Set the minimum date
              }}
            />
          )}
        </Grid>
        <Grid item xs={5} sm={4}>
          <ToggleButtonGroup
            value={durationType}
            exclusive
            onChange={handleDurationTypeChange}
            fullWidth
            size="small"
            orientation="horizontal"
            sx={{
              height: '56px',
              '& .MuiToggleButton-root': {
                padding: { xs: '6px 8px', sm: '6px 12px' },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                border: '1px solid rgba(0, 0, 0, 0.23)',
                height: '100%',
                '&.Mui-selected': {
                  backgroundColor: 'transparent',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(30, 30, 233, 0.08)',
                  }
                }
              }
            }}
          >
            <ToggleButton value="days">Days</ToggleButton>
            <ToggleButton value="endDate">End Date</ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>

      <TextField
        fullWidth
        label="Detail"
        placeholder="Detail of the request"
        multiline
        rows={3}
        value={details}
        onChange={(e) => {
          setDetails(e.target.value);
          if (e.target.value.trim()) setErrors({ ...errors, details: false });
        }}
        error={errors.details}
        helperText={errors.details ? "請填寫申請詳情" : ""}
        sx={{ mb: 3 }}
        required
      />

      <Button
        variant="contained"
        fullWidth
        onClick={handleWhatsAppSubmit} // Add onClick handler
        sx={{ mb: 2, backgroundColor: '#25D366', '&:hover': { backgroundColor: '#1DAE54' } }} // WhatsApp green color
        disabled={isSubmittingWhatsApp} // Disable button when submitting
      >
        {isSubmittingWhatsApp ? 'Submitting...' : 'WhatsApp Submit'}
      </Button>

      {/* Reintroduce the "加入 Google 日曪" button */}
      <Button
        variant="outlined"
        fullWidth
        onClick={addToGoogleCalendar}
        sx={{ mb: 2 }}
      >
        Add to Calendar ONLY
      </Button>

      {/* Add Calendar Dialog */}
      <Dialog
        open={openCalendarDialog}
        onClose={() => { }} // Prevent closing on outside click
      >
        <DialogTitle>Add to Calender?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            After sending the request for approvel, do you want the event to also be added to Calendar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCalendarDialog(false)}>No</Button>
          <Button onClick={addToGoogleCalendar} variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          severity={snackbarSeverity} // Updated to use snackbarSeverity
          onClose={() => setOpenSnackbar(false)}
        >
          {snackbarMessage} {/* Updated to use snackbarMessage */}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default RequestForm;
